'use client';

import { useCallback } from 'react';
import {
	User,
	OneShotUserAccountSubscriber,
	PublicKey,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { getOracleCrankIxs } from 'src/utils/oracle';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useRiskCeilingSlotClock } from 'src/hooks/useSlotClock';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useHandleTxChain } from 'src/hooks/velocityActions/transactions/useHandleTxChain';

/**
 * Hook for settling PnL for a specific market and user
 */
export const useSettlePnl = () => {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const { handleTxChain } = useHandleTxChain();
	const currentSlotRef = useCurrentSlotRef();
	const slotDurationMsRef = useSyncedRef(
		useRiskCeilingSlotClock().slotDurationMs
	);

	const settlePnl = useCallback(
		async (marketIndex: number, userPubKey?: PublicKey): Promise<boolean> => {
			if (!velocityClient || !walletAdapter) return false;
			const toastId = `settlepnl-${Date.now()}`;

			// if userPubKey is passed then settle that user, if not then settle the current connected user
			const pubKeyToSettle =
				userPubKey ?? (currentUserAccount?.pubKey as PublicKey);
			let userAccountToSettle = currentUserAccount?.client?.getUserAccount();
			if (!userAccountToSettle) return false;
			if (userPubKey) {
				const user = new User({
					velocityClient: velocityClient,
					userAccountPublicKey: pubKeyToSettle,
					accountSubscription: {
						type: 'custom',
						userAccountSubscriber: new OneShotUserAccountSubscriber(
							velocityClient.program,
							pubKeyToSettle
						),
					},
				});
				await user.subscribe();
				userAccountToSettle = user.getUserAccountOrThrow();
			}

			notify({
				type: 'info',
				message: 'Awaiting Transaction',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				const marketIds = [MarketId.createPerpMarket(marketIndex)];

				const optionalIxs = await getOracleCrankIxs(
					marketIds,
					velocityClient,
					walletAdapter.name,
					{
						current: currentSlotRef.current,
						durationMs: slotDurationMsRef.current,
					},
					undefined
				);

				const _tx = await handleTxChain(
					velocityClient.settlePNL(
						pubKeyToSettle,
						userAccountToSettle,
						marketIndex,
						getTxParams({
							marketId: MarketId.createPerpMarket(marketIndex),
						}),
						optionalIxs
					)
				);

				notify({
					type: 'success',
					message: 'Settled P&L.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					fallbackDescription: `Sorry, there was an unknown error settling your PnL.`,
					toastId,
					wallet: walletAdapter,
				});

				return false;
			}
		},
		[
			velocityClient,
			walletAdapter,
			getTxParams,
			currentUserAccount,
			handleTxChain,
		]
	);

	return { settlePnl };
};
